import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { IOptionProps } from "../../@types/reactSelect/IOption";
import { Loading } from "../../components/Loading/Index";
import { PDFEvaluationGlobal } from "../../components/Pdf/global";
import { PDFEvaluation } from "../../components/Pdf/microAreas";
import axios from "../../services/axios";
import "./styles.css";
import { useTranslation } from "react-i18next";

type ReportModel = "GLOBAL" | "MICRO_AREA";

function normalizeModel(value?: string | null): ReportModel {
  return value === "1" || value === "GLOBAL" ? "GLOBAL" : "MICRO_AREA";
}

function getRepeatedParams(searchParams: URLSearchParams, key: string) {
  const repeated = searchParams.getAll(key);
  if (repeated.length) return repeated;
  return (searchParams.get(key) || "").split(",").filter(Boolean);
}

function wait(ms: number) {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

function getScrollTarget(element: HTMLElement, scrollRoot: HTMLElement | null) {
  if (!scrollRoot) return element.offsetTop;

  const rootRect = scrollRoot.getBoundingClientRect();
  const elementRect = element.getBoundingClientRect();
  return elementRect.top - rootRect.top + scrollRoot.scrollTop;
}

export function PastureEvaluationReportRender() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [farm, setFarm] = useState<IOptionProps>();
  const [areas, setAreas] = useState<IOptionProps[]>([]);
  const [dataReport, setDataReport] = useState<any>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const readyEmittedRef = useRef(false);
  const pendingReadyRef = useRef(false);
  const warmUpDoneRef = useRef(false);

  const model = normalizeModel(searchParams.get("model"));
  const farmId = searchParams.get("selected_farm_id") || searchParams.get("farm_id");
  const shouldPrint = searchParams.get("print") === "1";
  const areaIds = useMemo(() => getRepeatedParams(searchParams, "area_ids"), [searchParams]);
  const microAreaIds = useMemo(() => getRepeatedParams(searchParams, "micro_area_ids"), [searchParams]);

  const finishReady = useCallback(() => {
    if (readyEmittedRef.current) return;
    readyEmittedRef.current = true;
    (window as any).__PASTEJO_REPORT_READY__ = true;
    window.parent?.postMessage({ type: "PASTEJO_REPORT_READY" }, window.location.origin);

    if (shouldPrint) {
      window.setTimeout(() => window.print(), 100);
    }
  }, [shouldPrint]);

  const emitReady = useCallback(() => {
    if (!warmUpDoneRef.current) {
      pendingReadyRef.current = true;
      return;
    }

    finishReady();
  }, [finishReady]);

  useEffect(() => {
    (window as any).__PASTEJO_REPORT_READY__ = false;
  }, []);

  useEffect(() => {
    if (!dataReport) return;

    let cancelled = false;

    async function warmUpLazyContent() {
      await wait(120);

      const scrollRoot = document.querySelector(".pasture-evaluation-report-render") as HTMLElement | null;
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 800;
      const scrollToPosition = async (scrollTop: number, delay = 90) => {
        window.scrollTo({ top: scrollTop, behavior: "auto" });
        scrollRoot?.scrollTo({ top: scrollTop, behavior: "auto" });
        await wait(delay);
      };

      const mapContainers = Array.from(document.querySelectorAll(".pdfPageMapContainer")) as HTMLElement[];
      for (const mapContainer of mapContainers) {
        if (cancelled) return;
        await scrollToPosition(Math.max(getScrollTarget(mapContainer, scrollRoot) - 80, 0), 450);
      }

      const scrollHeight = Math.max(
        document.body.scrollHeight,
        document.documentElement.scrollHeight,
        scrollRoot?.scrollHeight || 0,
      );
      const step = Math.max(Math.floor(viewportHeight * 1.2), 900);

      for (let scrollTop = 0; scrollTop <= scrollHeight && !cancelled; scrollTop += step) {
        await scrollToPosition(scrollTop);
      }

      if (!cancelled) {
        await scrollToPosition(0, 50);
        warmUpDoneRef.current = true;
        if (pendingReadyRef.current) finishReady();
      }
    }

    warmUpLazyContent();

    return () => {
      cancelled = true;
    };
  }, [dataReport, finishReady]);

  useEffect(() => {
    if (!farmId || !microAreaIds.length) {
      setError(t("pdfResidual.insufficientParameters"));
      setLoading(false);
      window.parent?.postMessage({ type: "PASTEJO_REPORT_ERROR" }, window.location.origin);
      return;
    }

    const reportParams = new URLSearchParams();
    if (model === "GLOBAL") reportParams.set("farm_id", farmId);
    microAreaIds.forEach((microAreaId) => reportParams.append("micro_area_ids", microAreaId));

    Promise.all([
      axios.get("farms/user"),
      axios.get("areas/user"),
      axios.get(`tasks/pasture-evaluation/report?${reportParams.toString()}`),
    ])
      .then(([farmsResponse, areasResponse, reportResponse]) => {
        const selectedFarm = farmsResponse.data.find((farmItem: any) => String(farmItem.id) === String(farmId));
        const selectedAreas = areasResponse.data.filter((area: any) =>
          areaIds.some((areaId) => String(areaId) === String(area.id)),
        );

        setFarm(selectedFarm ? { value: selectedFarm.id, label: selectedFarm.name, info: selectedFarm } : undefined);
        setAreas(selectedAreas.map((area: any) => ({ value: area.id, label: area.name, info: area })));
        setDataReport(reportResponse.data);
      })
      .catch(() => {
        setError(t("pdfResidual.loadError"));
        window.parent?.postMessage({ type: "PASTEJO_REPORT_ERROR" }, window.location.origin);
      })
      .finally(() => setLoading(false));
  }, [areaIds, farmId, microAreaIds, model]);

  if (error || !farm || !dataReport) {
    return <span></span>;
  }

  return (
    <main className="pasture-evaluation-report-render">
      {model === "GLOBAL" ? (
        <PDFEvaluationGlobal
          farm={farm}
          areas={areas}
          dataReport={dataReport as IReportGlobalPastureEvaluator}
          onReady={emitReady}
        />
      ) : (
        <PDFEvaluation
          farm={farm}
          areas={areas}
          dataReport={dataReport as IReportMicroAreaPastureEvaluator}
          onReady={emitReady}
        />
      )}
    </main>
  );
}
